import { track } from '@sloyd/analytics';
import { actions, selectors } from '@sloyd/store-base';
import { FormOrder, prompt, searchAndFilter, toast } from '@sloyd/ui';
import { call, delay, fork, put, select, takeEvery } from 'saga-ts';
import { $a, guid4, invokeEvent, l, sortBy } from 'shared-base';
import ContentGeneratorDetailsContainer from '../components/ContentGeneratorDetails/ContentGeneratorDetails.container';
import { dataGuard } from './helpers/guards';
import { getIdAndName } from './helpers/idAndName';
import { navToCurrentProject, navToModel } from './helpers/navigation';

export const saga = {
  id: 'sloyd.generators',
  type: 'entity',
  root: root,
  trigger: {
    actionTypes: ['GENERATORS'],
  },
};

type Verb =
  | 'playground' //
  | 'pick'
  | 'nextPrev'
  | 'orderCustom'
  | 'addToLibrary';

type Action = {
  type: 'GENERATORS';
  verb: Verb;
  id: string;
  payload: Json;
};

const map: Record<Verb, any> = {
  pick: pickGenerator,
  addToLibrary: addToLibrary,
  playground: openPlayground,
  orderCustom: orderCustom,
  nextPrev: nextPrev,
};

const analyitcsMapGenerators: any = {
  pick: 'Pick Generator',
  addToLibrary: 'Add Generator To Library',
  playground: 'Open Generator Playground',
  nextPrev: 'Next/Previous Generator',
  orderCustom: 'Order Custom Generator',
};

export function* orderCustom(action: Action, _generator: IGenerator) {
  try {
    const user = yield* select(selectors.raw.$rawUser);
    const { email } = user ?? {};

    const { value, didCancel } = yield prompt.custom({
      title: 'Custom generator',
      component: FormOrder,
      componentProps: {
        email,
      },
    });

    if (!value || didCancel) return;

    track('Order Custom Generator Submitted', { originalEmail: email, ...value });
    toast.show('Thank you for your quote request! We will get back to you soon.');
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* nextPrev(action: Action, _generator: IGenerator) {
  const { payload } = action;
  const { delta } = payload;

  const generators = yield* select(selectors.base.$generators);
  const generator = yield* select(selectors.single.$currentGenerator);
  const appState = yield* select(selectors.raw.$rawAppState);
  const { q, tag } = appState;

  try {
    const results = searchAndFilter(generators, ['id', 'name', 'tags'], q, tag);
    const indexOf = results.sort(sortBy('order')).findIndex((g) => g.id === generator.id);

    const nextIndex = indexOf + delta;
    const nextGenerator = results[nextIndex];
    if (!nextGenerator) return;

    yield put(actions.currentIds.patch({ generatorId: nextGenerator.id }));
    invokeEvent('generator/change', { id: nextGenerator.id });
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* openPlayground(action: Action, generator: IGenerator) {
  const { payload } = action;
  const { q, tag } = payload;

  if (!generator) return;
  // const show = prefs('showGeneratorDetails');
  const show = true;

  yield put(actions.appState.patch({ q, tag }));
  yield put(actions.currentIds.patch({ generatorId: generator.id }));

  if (show) {
    yield prompt.custom({
      title: 'Generator Details',
      component: ContentGeneratorDetailsContainer,
      componentProps: {
        id: generator.id,
      },
    });
  } else {
    invokeEvent('nav', { path: `/playground/generators/${generator.id}` });
  }
}

export function* pickGenerator(action: Action, generator: IGenerator) {
  try {
    if (!generator) return;
    const { id, name, paletteId } = generator;

    yield navToCurrentProject();

    const modelId = guid4();

    yield put(
      $a('MODELS', 'create', modelId, {
        name,
        generatorId: id,
        paletteId,
      })
    );

    yield delay(50);

    yield navToModel(modelId);
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* addToLibrary(action: Action, generator: IGenerator) {
  try {
    if (!generator) return;
    const { id, name } = generator;
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* generators(action: any) {
  const { verb, id, origin } = action;
  yield delay(10);

  const saga = (map as any)[verb];

  if (!saga) {
    return;
  }

  l(`saga|saga.generators ${verb} on ${id}`);

  yield* dataGuard();

  const generator = yield* select(selectors.single.$generator, id);
  const idAndName = yield* call(getIdAndName, { generator });
  track(analyitcsMapGenerators[verb], { origin, ...idAndName });

  yield* fork(saga, action, generator);
}

export function* root() {
  yield takeEvery('GENERATORS', generators);
}
